import { leaveNow } from "../ui/unsaved";
import { countdown, idlePhase } from "../../server/rules/sign-in";
import { api, el, toast } from "../ui/dom";
import { icon } from "../ui/icons";
import type { Me } from "./types";
import { dropMe } from "./me-cache";

// Sign out when idle (brief 1E-15, mockup v71, DECISIONS 10/28 "Idle sign-out works like a bank"):
// after the company's idle minutes (the field app has its own) a warning counts down, "Signing out
// in 0:59", with Stay signed in and Sign out. Stay signed in keeps the session; when the countdown
// ends the session ends and the sign-in page opens. Signing in again (the usual way) opens the
// same page, with anything typed and not yet sent put back from the page's drafts.
//
// Every tab of the app shares the last activity (localStorage), so activity in one tab counts in
// all; each tab shows the warning, and Stay signed in in one clears it in all. One tab records the
// warning and the sign-out in the history.
//
// The server ends the session too (brief 1E-19c): on the next request after the minutes plus the
// warning, tab open or closed. Opening the app again then shows the sign-in page, and signing in
// opens the page that was asked for (keepReturnPage).

const INPUT = ["keydown", "pointerdown", "mousemove", "wheel", "touchstart", "scroll"] as const;
const LAST = "fc-idle-last";
const WARNED = "fc-idle-warned";
const OUT = "fc-idle-out";
/** This tab's page and its typing, kept for the sign-in after an idle sign-out (per tab). */
const RETURN = "fc-idle-return";
const DRAFTS = "fc-idle-drafts";
/** The walks shorten the idle time (only where the adapters are fakes: me.idle.testTimer). */
export const TEST_TIMER_KEY = "fc-idle-test-seconds";

const read = (store: () => Storage, key: string): string | null => {
  try {
    return store().getItem(key);
  } catch {
    return null;
  }
};
const write = (store: () => Storage, key: string, value: string | null) => {
  try {
    if (value === null) store().removeItem(key);
    else store().setItem(key, value);
  } catch {
    /* no storage: this tab works on its own */
  }
};
const local = () => localStorage;
const session = () => sessionStorage;

/** What's typed on the page: each text box with an id and something in it. */
function draftsOnPage(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'textarea[id], input[id]:not([type]), input[id][type="text"], input[id][type="email"], input[id][type="tel"], input[id][type="number"], input[id][type="date"]',
  ))
    if (f.value.trim()) out[f.id] = f.value;
  return out;
}

/** Signed out when the app opens on a page: that page opens after signing in (this tab). */
export function keepReturnPage(page: string) {
  if (page !== "/" && page.startsWith("/") && !page.startsWith("//")) write(session, RETURN, page);
}

/**
 * After signing in again: the page the idle sign-out left (answers it, once), so the shell opens
 * there instead of Home.
 */
export function takeReturnPage(): string | null {
  const page = read(session, RETURN);
  write(session, RETURN, null);
  return page && page.startsWith("/") && !page.startsWith("//") ? page : null;
}

/**
 * Puts the typing back once the page has drawn its boxes: each saved box that's there and still
 * empty gets its text, as if typed. Gives up after 20 seconds.
 */
export function restoreDrafts() {
  const saved = read(session, DRAFTS);
  write(session, DRAFTS, null);
  if (!saved) return;
  let left: Map<string, string>;
  try {
    left = new Map(Object.entries(JSON.parse(saved) as Record<string, string>));
  } catch {
    return;
  }
  const fill = () => {
    for (const [id, value] of left) {
      const f = document.getElementById(id);
      if (!(f instanceof HTMLInputElement || f instanceof HTMLTextAreaElement)) continue;
      if (!f.value) {
        f.value = value;
        f.dispatchEvent(new Event("input", { bubbles: true }));
      }
      left.delete(id);
    }
    return !left.size;
  };
  if (fill()) return;
  const watch = new MutationObserver(() => {
    if (fill()) watch.disconnect();
  });
  watch.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => watch.disconnect(), 20_000);
}

let showing: { root: HTMLElement; count: HTMLElement } | null = null;

/** The warning, as the mockup draws it. Settings' See the warning shows it with `preview`. */
export function warningBox(o: {
  minutes: number;
  seconds: number;
  stay: () => void;
  out: () => void;
}): { root: HTMLElement; count: HTMLElement } {
  const count = el("span", { id: "idle-count" }, countdown(o.seconds));
  const stay = el("button", { type: "button", class: "btn pri" }, "Stay signed in");
  const out = el("button", { type: "button", class: "btn" }, "Sign out");
  stay.addEventListener("click", o.stay);
  out.addEventListener("click", o.out);
  const root = el(
    "div",
    { id: "idleov", class: "scrim idleov" },
    el(
      "div",
      {
        class: "modal idlebox",
        role: "alertdialog",
        "aria-modal": "true",
        "aria-labelledby": "idle-h",
        "aria-describedby": "idle-d",
      },
      el("h2", { id: "idle-h" }, icon("clock"), " Signing out in ", count),
      el(
        "p",
        { class: "small muted", id: "idle-d", style: "margin:0" },
        `No activity for ${o.minutes} minute${o.minutes === 1 ? "" : "s"}.`,
      ),
      el("div", { class: "actions", style: "justify-content:center;margin-top:10px" }, stay, out),
    ),
  );
  document.body.append(root);
  stay.focus();
  return { root, count };
}

/**
 * Watches for no activity in any tab and warns, then signs out, after the minutes (the field
 * app's while it shows).
 */
export function startIdle(me: () => Me) {
  let lastInput = Date.now();
  let lastWrite = 0;
  let leaving = false;
  const field = () => document.body.classList.contains("in-field");
  const app = () => (field() ? "field" : "office");
  const minutes = () => (field() ? me().idle.fieldMinutes : me().idle.officeMinutes);
  const idleMs = () => {
    if (me().idle.testTimer) {
      const s = Number(read(session, TEST_TIMER_KEY));
      if (s > 0) return s * 1000;
    }
    return minutes() * 60_000;
  };
  // Only Stay signed in is the person doing something (brief 1E-19c).
  const tell = (event: "idle_warned" | "idle_stayed" | "idle_signed_out") =>
    api<{ minutes?: number }>(
      "POST",
      "/api/me/idle",
      { event, app: app() },
      { background: event !== "idle_stayed" },
    );
  const active = (now = Date.now()) => {
    lastInput = now;
    if (now - lastWrite > 1000) {
      lastWrite = now;
      write(local, LAST, String(now));
    }
  };
  const hide = () => {
    showing?.root.remove();
    showing = null;
  };

  // While the warning shows, only Stay signed in counts as activity.
  for (const type of INPUT)
    document.addEventListener(type, () => (showing ? undefined : active()), {
      passive: true,
      capture: true,
    });
  active();

  setInterval(() => {
    if (leaving) return;
    const last = Math.max(lastInput, Number(read(local, LAST)) || 0);
    const now = Date.now();
    const at = idlePhase(last, now, idleMs(), me().idle.warnSeconds * 1000);
    if (at.phase === "active") return hide();
    if (at.phase === "warning") {
      if (!showing)
        showing = warningBox({
          minutes: minutes(),
          seconds: at.secondsLeft,
          stay: () => {
            active(Date.now());
            write(local, LAST, String(Date.now()));
            hide();
            toast("Still signed in");
            void tell("idle_stayed");
          },
          out: () => {
            leaving = true;
            leaveNow();
            dropMe();
            void fetch("/api/auth/signout", { method: "POST", cache: "no-store" }).finally(() =>
              location.assign("/"),
            );
          },
        });
      else showing.count.textContent = countdown(at.secondsLeft);
      // One tab records the warning for this stretch of no activity.
      if (read(local, WARNED) !== String(last)) {
        write(local, WARNED, String(last));
        void tell("idle_warned");
      }
      return;
    }
    // The warning ran out: keep this tab's page and typing, end the session, sign in again.
    leaving = true;
    // Signed out for being idle: the browser doesn't ask; the typing is kept for after.
    leaveNow();
    dropMe();
    write(session, RETURN, location.pathname + location.search);
    write(session, DRAFTS, JSON.stringify(draftsOnPage()));
    const first = read(local, OUT) !== String(last);
    if (first) write(local, OUT, String(last));
    void (first ? tell("idle_signed_out") : Promise.resolve()).finally(() =>
      location.assign(`/?signin=idle&m=${minutes()}`),
    );
  }, 1000);
}
